import React, { useState, useEffect, useContext } from "react";
import { getUserData } from "../services/userService";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import ConfirmModal from "./ConfirmModal";
import { NotificationsContext } from "../hooks/notificationsContext";
import "./FoundCompanyTab.css";

const REGION_MODIFIERS = {
  "Africa": { partner: ["AS", "EU"], good: ["-5% tax on gold sales", "Bonds mature 1 day earlier"], bad: ["-10% interest on bonds"] },
  "Asia": { partner: ["AF", "AU", "SA"], good: ["-10% tax on currency exchange"], bad: ["Bonds require double the investment"] },
  "Australia": { partner: ["AS", "NA"], good: ["+20% interest on bonds"], bad: ["+5% tax on crypto sales"] },
  "Europe": { partner: ["AF", "AU", "NA"], good: ["+10% to yield on gold mining"], bad: ["+10% tax on all sales"] },
  "North America": { partner: ["AU", "EU", "SA"], good: ["-50% start-up costs", "+10% issued dividends"], bad: ["Bonds mature 1 day later"] },
  "South America": { partner: ["AS", "NA"], good: ["-10% tax on crypto sales"], bad: ["+5% tax on stock sales"] },
};

const REGION_MAP = {
  "Africa": "AF",
  "Asia": "AS",
  "Australia": "AU",
  "Europe": "EU",
  "North America": "NA",
  "South America": "SA"
};

export default function FoundCompanyTab({ userId }) {
  const [name, setName] = useState("");
  const [ticker, setTicker] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("Africa");
  const [description, setDescription] = useState("");
  const [userLevel, setUserLevel] = useState("Basic");
  const [creating, setCreating] = useState(false);
  const [cost, setCost] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const { addNotification } = useContext(NotificationsContext);

  useEffect(() => {
    async function fetchUserLevel() {
      if (!userId) return;
      const data = await getUserData(userId);
      setUserLevel(data.userLevel || "Basic");
    }
    fetchUserLevel();
  }, [userId]);

  useEffect(() => {
    async function fetchGlobalTax() {
      try {
        const globalDocRef = doc(db, "Global_Vars", "Main");
        const globalDocSnap = await getDoc(globalDocRef);
        const globalData = globalDocSnap.data();
        const baseRate = globalData?.globalBaseTaxRate || 0;

        let calculatedCost = Math.round((baseRate / 100) * 450000 * 100) / 100;
        if (REGION_MAP[selectedRegion] === "NA") {
          calculatedCost = Math.round(calculatedCost / 2 * 100) / 100;
        }
        setCost(calculatedCost);
      } catch (err) {
        console.error("Failed to fetch global tax rate:", err);
      }
    }
    fetchGlobalTax();
  }, [selectedRegion]);

  const isTickerValid = (() => {
    const alphanumeric = /^[a-zA-Z0-9]+$/;
    if (!alphanumeric.test(ticker)) return false;
    const len = ticker.length;

    switch (userLevel) {
      case "Moderator":
      case "Admin":
      case "Power":
        return len >= 2 && len <= 4;
      case "Premium":
        return len >= 3 && len <= 4;
      case "Preferred":
        return len === 4;
      default:
        return false;
    }
  })();

  const isFormValid =
    name.length > 0 &&
    isTickerValid &&
    selectedRegion &&
    userLevel !== "Basic";

  const handleConfirmCreate = async () => {
    setShowConfirm(false);
    setCreating(true);

    try {
      const token = await auth.currentUser.getIdToken(true);
      const regionCode = REGION_MAP[selectedRegion] || "AF";

      const res = await fetch(
        "https://foundnewcompany-5dhfwyjvzq-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            Name: name,
            Ticker: ticker.toUpperCase(),
            Region: regionCode,
            Description: description.trim(),
          }),
        }
      );

      if (!res.ok) throw new Error(await res.text());

      setName("");
      setTicker("");
      setSelectedRegion("Africa");
      setDescription("");

      addNotification("Company founded.");
    } catch (err) {
      console.error(err);
      addNotification(`Failed to create company: ${err.message}`, "error");
    } finally {
      setCreating(false);
    }
  };
  
return (
  <>
    <div className="found-company-container" style={{
      width: "360px", background: "rgba(0, 30, 30, 0.95)", border: "1px solid rgba(80, 227, 194, 0.3)",
      padding: "20px", fontFamily: "'JetBrains Mono', monospace", boxSizing: "border-box",
      position: "relative", overflow: "visible"
    }}>
      <h2 style={{ color: "#50E3C2", fontSize: "1rem", margin: "0 0 15px 0", borderBottom: "1px solid rgba(80, 227, 194, 0.2)", paddingBottom: "8px" }}>
        FOUND COMPANY
      </h2>

      {/* 1. Identity Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", gap: "12px", marginBottom: "15px" }}>
        <div>
          <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "4px" }}>COMPANY NAME</label>
          <input type="text" maxLength={32} value={name} onChange={(e) => setName(e.target.value)}
            style={{ width: "100%", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(80,227,194,0.2)", color: "#fff", padding: "8px", boxSizing: "border-box", outline: "none", fontFamily: "inherit" }}
          />
        </div>
        <div>
          <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "4px" }}>TICKER</label>
          <input type="text" maxLength={4} value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())}
            style={{ width: "100%", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(80,227,194,0.2)", color: "#50E3C2", padding: "8px", textAlign: "center", boxSizing: "border-box", outline: "none", fontFamily: "inherit" }}
          />
        </div>
      </div>

{/* 2. Region Selection */}
      <div style={{ marginBottom: "15px" }}>
        <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "6px" }}>REGION SELECTION</label>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(3, 1fr)", 
          gap: "6px",
          width: "100%"
        }}>
          {Object.keys(REGION_MODIFIERS).map((region, idx) => {
            let tooltipAlign = { left: "50%", transform: "translateX(-50%)" };
            if (idx === 0 || idx === 3) tooltipAlign = { left: "0", transform: "none" };
            if (idx === 2 || idx === 5) tooltipAlign = { right: "0", transform: "none" };

            return (
              <div key={region} className="tooltip-container" style={{ position: "relative", width: "100%" }}>
                <label style={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    width: "100%",
                    padding: "8px 0", 
                    cursor: "pointer", 
                    fontSize: "0.6rem",
                    border: "1px solid", 
                    borderColor: selectedRegion === region ? "#50E3C2" : "rgba(255,255,255,0.1)",
                    background: selectedRegion === region ? "rgba(80, 227, 194, 0.15)" : "transparent",
                    color: selectedRegion === region ? "#50E3C2" : "#666",
                    boxSizing: "border-box"
                  }}>
                  <input type="radio" name="region" value={region} checked={selectedRegion === region}
                    onChange={() => setSelectedRegion(region)} style={{ display: "none" }}
                  />
                  {region.toUpperCase()}
                </label>
{/* Tooltip */}
<div className="tooltip-text" style={{ 
  ...tooltipAlign,
  position: "absolute", 
  bottom: "135%", 
  width: "260px", 
  background: "#0a1f1f",
  border: "1px solid #50E3C2", 
  borderLeft: "3px solid #50E3C2",
  padding: "12px", 
  zIndex: 1000,
  fontSize: "0.6rem", 
  display: "flex", 
  flexDirection: "column",
  pointerEvents: "none",
  height: "max-content",
  boxSizing: "border-box",
  whiteSpace: "normal",
  overflow: "visible",
  textAlign: "left"
}}>
  <div style={{ 
    color: "#50E3C2", 
    fontWeight: "bold", 
    borderBottom: "1px solid rgba(80, 227, 194, 0.3)", 
    marginBottom: "8px",
    paddingBottom: "4px",
    width: "100%",
    display: "block"
  }}>
    {region.toUpperCase()} MODIFIERS
  </div>

  <div style={{ paddingBottom: "6px", width: "100%", display: "block", lineHeight: "1.3" }}>
    <span className="good-modifiers" style={{ margin: 0 }}>STRENGTHS</span> 
    <span style={{ color: "#eee" }}> {REGION_MODIFIERS[region].good.join("; ")}</span>
  </div>
  
  <div style={{ width: "100%", display: "block", lineHeight: "1.3" }}>
    <span className="bad-modifiers" style={{ margin: 0 }}>DRAWBACKS</span> 
    <span style={{ color: "#eee" }}> {REGION_MODIFIERS[region].bad.join("; ")}</span>
  </div>
</div>
              </div>
            );
          })}
        </div>
      </div>

{/* 3. Description Section */}
      <div style={{ marginBottom: "15px", width: "100%" }}>
        <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "4px" }}>DESCRIPTION</label>
        <textarea 
          value={description} 
          onChange={(e) => setDescription(e.target.value)}
          rows={1}
          style={{ 
            width: "100%", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(80,227,194,0.2)",
            color: "#eee", padding: "10px", resize: "none", boxSizing: "border-box", 
            outline: "none", fontFamily: "inherit", fontSize: "0.75rem", lineHeight: "1.4"
          }}
          placeholder="OPTIONAL"
        />
      </div>

{/* 4. Capital Row (Fee) */}
      <div style={{ 
        display: "flex", justifyContent: "space-between", alignItems: "center",
        fontSize: "0.7rem", padding: "10px", background: "rgba(0,0,0,0.4)", marginBottom: "10px", 
        border: "1px solid rgba(255,255,255,0.05)", width: "100%", boxSizing: "border-box" 
      }}>
        <span style={{ color: "#888" }}>FOUNDING FEE:</span>
        <span style={{ color: "#ff7b72", fontWeight: "bold" }}>${cost.toLocaleString()}</span>
      </div>

      {/* ACTION AREA */}
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}>
        
        <button 
          onClick={() => setShowConfirm(true)} 
          /* Disable if not valid, if creating, or if they are Basic rank */
          disabled={!isFormValid || creating || userLevel?.toLowerCase() === "basic"}
          style={{ 
            width: "100%", 
            padding: "12px", 
            background: (userLevel?.toLowerCase() === "basic") ? "#222" : "#50E3C2", 
            color: (userLevel?.toLowerCase() === "basic") ? "#555" : "#000", 
            border: (userLevel?.toLowerCase() === "basic") ? "1px solid #444" : "none", 
            fontWeight: "bold", 
            fontFamily: "inherit",
            cursor: (userLevel?.toLowerCase() === "basic" || !isFormValid || creating) ? "not-allowed" : "pointer", 
            opacity: (!isFormValid || creating) ? 0.6 : 1,
            transition: "all 0.2s ease",
            textTransform: "uppercase"
          }}
        >
          {creating ? "FOUNDING..." : "FOUND"}
        </button>

        {/* STATUS FOOTER - Case-insensitive check */}
{/* STATUS FOOTER - Clean text version */}
        {userLevel?.toLowerCase() === "basic" ? (
          <div style={{ 
            textAlign: "center", 
            fontSize: "0.6rem", 
            color: "#FF4D4D", 
            letterSpacing: "1px",
            textTransform: "uppercase",
            marginTop: "4px",
            fontWeight: "bold",
            opacity: 0.9,
            fontFamily: "'JetBrains Mono', monospace"
          }}>
            Preferred+ required
          </div>
        ) : !isFormValid && (
          <div style={{ 
            textAlign: "center", 
            fontSize: "0.55rem", 
            color: "#666", 
            marginTop: "4px",
            textTransform: "uppercase"
          }}>
            Input required
          </div>
        )}
      </div>

    </div> {/* Closes found-company-container */}

    {showConfirm && (
      <ConfirmModal 
        title="CONFIRM FOUNDATION" 
        message={`Authorize creation of ${name} [${ticker}] in ${selectedRegion}? Total fee: $${cost.toLocaleString()}`}
        confirmText="CONFIRM" 
        cancelText="CANCEL" 
        onConfirm={handleConfirmCreate} 
        onCancel={() => setShowConfirm(false)} 
        isProcessing={creating}
      />
    )}
  </>
);
}